import axios from 'axios';
import Vue from 'vue';

const AI_API_BASE_URL = 'https://api-my-oracle.nicesky-271038f1.brazilsouth.azurecontainerapps.io';
const MODULE_NAME = 'MyConfig';

/**
 * Gets both PHP and .NET authentication tokens from cookies / localStorage
 */
function getAuthTokens() {
  let netToken = '';
  let phpToken = '';

  try {
    const userInfo = Vue.prototype.$cookies?.get('userInfo');
    if (userInfo) {
      netToken = userInfo.temporaryToken || '';
      phpToken = userInfo.accessToken || '';
    }
  } catch (e) {
    // ignore
  }

  if (!phpToken) {
    phpToken = localStorage.getItem('accessToken') || '';
  }

  return { netToken, phpToken };
}

/**
 * siteId from userInfo cookie (currentSite) or localStorage fallback
 */
function getSiteId() {
  try {
    const userInfo = Vue.prototype.$cookies?.get('userInfo');
    if (userInfo?.currentSite != null && userInfo.currentSite !== '') {
      const parsed = parseInt(userInfo.currentSite, 10);
      return Number.isNaN(parsed) ? userInfo.currentSite : parsed;
    }
  } catch (e) {
    // ignore
  }

  try {
    const site = localStorage.getItem('currentSite');
    if (!site) return null;
    const parsedJson = (() => {
      try {
        return JSON.parse(site);
      } catch (e) {
        return site;
      }
    })();
    const value = typeof parsedJson === 'object' && parsedJson !== null
      ? (parsedJson.id ?? parsedJson.guid ?? null)
      : parsedJson;
    if (value == null || value === '') return null;
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? value : parsed;
  } catch (e) {
    return null;
  }
}

/**
 * accountId from Vuex (permissions) or userInfo cookie when available
 */
function getAccountId() {
  try {
    const store = Vue.prototype.$store;
    const fromStore =
      store?.getters?.['auth/getAccountId'] ??
      store?.state?.auth?.accountId;
    if (fromStore != null && fromStore !== '') {
      const parsed = parseInt(fromStore, 10);
      return Number.isNaN(parsed) ? fromStore : parsed;
    }
  } catch (e) {
    // ignore
  }

  try {
    const userInfo = Vue.prototype.$cookies?.get('userInfo');
    const fromCookie =
      userInfo?.accountId ??
      userInfo?.userData?.accountId ??
      null;
    if (fromCookie != null && fromCookie !== '') {
      const parsed = parseInt(fromCookie, 10);
      return Number.isNaN(parsed) ? fromCookie : parsed;
    }
  } catch (e) {
    // ignore
  }

  return null;
}

/**
 * Gets currently saved conversation ID or null if new conversation
 */
export function getStoredConversationId() {
  return sessionStorage.getItem('ai_conversation_id') || null;
}

/**
 * Saves conversation ID returned from MyOracle backend
 */
export function saveConversationId(convId) {
  if (convId) {
    sessionStorage.setItem('ai_conversation_id', convId);
  }
}

/**
 * Clears current conversation ID to start a fresh chat (sends null on next call)
 */
export function resetConversationId() {
  sessionStorage.removeItem('ai_conversation_id');
}

function buildAuthHeaders(contentType) {
  const { netToken, phpToken } = getAuthTokens();
  const headers = {};

  if (contentType) {
    headers['Content-Type'] = contentType;
  }

  if (netToken) {
    headers['Authorization'] = `Bearer ${netToken}`;
    headers['X-NET-Token'] = netToken;
  } else if (phpToken) {
    headers['Authorization'] = `Bearer ${phpToken}`;
  }

  if (phpToken) {
    headers['X-PHP-Token'] = phpToken;
  }

  return headers;
}

/**
 * Sends a text message prompt to MyOracle AI (/api/chat)
 * @param {string} message
 * @param {{ operationId?: string|number|null }} [options]
 */
export async function sendAiPrompt(message, options = {}) {
  const convId = getStoredConversationId();
  const siteId = getSiteId();
  const accountId = getAccountId();

  const payload = {
    message: message,
    modulo: MODULE_NAME,
    conversationId: convId, // null on first call
    siteId: siteId,
    accountId: accountId
  };

  if (options.operationId != null && options.operationId !== '') {
    payload.operationId = options.operationId;
  }

  try {
    const response = await axios.post(`${AI_API_BASE_URL}/api/chat`, payload, {
      headers: buildAuthHeaders('application/json'),
      timeout: 30000
    });

    return processBackendResponse(response.data);
  } catch (error) {
    console.error('[MyOracle AI] Erro na requisição /api/chat:', error);
    if (error.response && error.response.data) {
      return processBackendResponse(error.response.data);
    }
    throw error;
  }
}

/**
 * Sends a WAV audio file (multipart/form-data) to MyOracle AI (/api/chat/audio).
 * Expects OpenAI Realtime 2.1 format: WAV PCM, mono, 16-bit, 24 kHz.
 * The file is sent as `File` (not `message`).
 * @param {Blob} audioBlob
 * @param {{ operationId?: string|number|null }} [options]
 */
export async function sendAiAudio(audioBlob, options = {}) {
  const convId = getStoredConversationId();
  const siteId = getSiteId();
  const accountId = getAccountId();

  const formData = new FormData();
  const wavBlob =
    audioBlob instanceof Blob
      ? audioBlob
      : new Blob([audioBlob], { type: 'audio/wav' });

  formData.append('File', wavBlob, 'audio.wav');
  formData.append('Modulo', MODULE_NAME);

  if (convId) {
    formData.append('ConversationId', convId);
  }

  if (siteId != null && siteId !== '') {
    formData.append('SiteId', siteId);
  }

  if (accountId != null && accountId !== '') {
    formData.append('AccountId', accountId);
  }

  if (options.operationId != null && options.operationId !== '') {
    formData.append('OperationId', options.operationId);
  }

  try {
    const response = await axios.post(`${AI_API_BASE_URL}/api/chat/audio`, formData, {
      headers: buildAuthHeaders(),
      timeout: 45000
    });

    return processBackendResponse(response.data);
  } catch (error) {
    console.error('[MyOracle AI] Erro na requisição /api/chat/audio:', error);
    if (error.response && error.response.data) {
      return processBackendResponse(error.response.data);
    }
    throw error;
  }
}

/**
 * Extracts conversationId and text response from MyOracle backend
 */
function processBackendResponse(data) {
  if (!data) return 'Sem resposta do servidor de IA.';

  // If object has conversationId, save it for subsequent calls
  if (typeof data === 'object') {
    if (data.conversationId) {
      saveConversationId(data.conversationId);
    }
    if (data.response) return data.response;
    if (data.message) return data.message;
    if (data.reply) return data.reply;
    if (data.text) return data.text;
    if (data.content) return data.content;
  }

  if (typeof data === 'string') return data;

  return JSON.stringify(data);
}
